import {
  addArea,
  areaTells,
  getAreas,
  InvalidInput,
  Pick,
  removeArea,
  type AreaTell,
  type Plan,
  type TripArea,
} from '@trippath/shared';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { dayRange } from '../../lib/countryFacts';

/** The radii offered, in metres. */
const RADII = [200, 1000, 5000] as const;

/** The places of a trip an area may be drawn around: each stop, and each room with a place. */
function centres(
  plan: Plan,
  language: string,
): { value: string; label: string; lat: number; lon: number }[] {
  return plan.stops.flatMap((stop) => [
    ...(stop.place
      ? [
          {
            value: stop.id,
            label: `${stop.city} · ${dayRange(stop.start, stop.end, language)}`,
            ...stop.place,
          },
        ]
      : []),
    ...stop.bookings.flatMap((booking) =>
      booking.kind === 'stay' && booking.place
        ? [
            {
              value: booking.id,
              label: `${booking.name} · ${stop.city}`,
              lat: booking.place.lat,
              lon: booking.place.lon,
            },
          ]
        : [],
    ),
  ]);
}

/** `200 m`, `5 km`. */
function radiusOf(metres: number): string {
  return metres < 1000 ? `${String(metres)} m` : `${String(metres / 1000)} km`;
}

/**
 * The trip's areas (geofences): a circle around a stop or a room, and whether arriving, leaving
 * or both are told to everyone when a traveller's phone recording its way crosses it.
 */
export function Areas({ plan, editable }: { plan: Plan; editable: boolean }) {
  const { t, i18n } = useTranslation();
  const ids = useId();
  const trip = plan.trip.id;
  const [areas, setAreas] = useState<TripArea[] | null>(null);
  const [centre, setCentre] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [radius, setRadius] = useState<number>(RADII[0]);
  const [tell, setTell] = useState<AreaTell>('arrive');
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => {
    getAreas(trip).then(setAreas, () => {
      setAreas([]);
    });
  }, [trip]);
  const places = centres(plan, i18n.language);
  const picked = places.find((one) => one.value === centre);
  const done = (job: Promise<TripArea[]>) => {
    setFailed(null);
    job.then(
      (found) => {
        setAreas(found);
        setName('');
        setCentre(null);
      },
      (problem: unknown) => {
        setFailed(t(problem instanceof InvalidInput ? 'areas.invalid' : 'areas.failed'));
      },
    );
  };
  return (
    <section className="panel settings-card" aria-labelledby={`${ids}-title`}>
      <h2 id={`${ids}-title`}>{t('areas.title')}</h2>
      <p className="quiet">{t('areas.lead')}</p>
      {areas !== null && areas.length === 0 && <p className="quiet">{t('areas.none')}</p>}
      {areas !== null && areas.length > 0 && (
        <ul className="areas-list">
          {areas.map((area) => (
            <li key={area.id}>
              <span>
                <b>{area.name}</b>
                <span className="quiet">
                  {' · '}
                  {radiusOf(area.radius)} ·{' '}
                  {t('areas.told', { what: t(`areas.tells.${area.tell}`) })}
                </span>
              </span>
              {editable && (
                <button
                  type="button"
                  className="button button-small"
                  aria-label={t('areas.remove', { name: area.name })}
                  onClick={() => {
                    done(removeArea(trip, area.id));
                  }}
                >
                  {t('areas.removeShort')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {editable && (
        <div className="areas-form">
          <Pick
            label={t('areas.around')}
            searchable={false}
            placeholder={t('areas.aroundPick')}
            options={places.map((one) => ({ value: one.value, label: one.label }))}
            value={centre}
            onChange={setCentre}
          />
          <div className="field">
            <label className="field-label" htmlFor={`${ids}-name`}>
              {t('areas.name')}
            </label>
            <input
              id={`${ids}-name`}
              className="text-input"
              type="text"
              maxLength={100}
              value={name}
              placeholder={picked?.label}
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
          </div>
          <div
            className="segmented segmented-small"
            role="group"
            aria-label={t('areas.radius')}
          >
            {RADII.map((one) => (
              <button
                key={one}
                type="button"
                aria-pressed={radius === one}
                onClick={() => {
                  setRadius(one);
                }}
              >
                {radiusOf(one)}
              </button>
            ))}
          </div>
          <div className="segmented segmented-small" role="group" aria-label={t('areas.tell')}>
            {areaTells.map((one) => (
              <button
                key={one}
                type="button"
                aria-pressed={tell === one}
                onClick={() => {
                  setTell(one);
                }}
              >
                {t(`areas.tells.${one}`)}
              </button>
            ))}
          </div>
          {failed !== null && (
            <p className="form-failed" role="alert">
              {failed}
            </p>
          )}
          {picked === undefined ? (
            <button type="button" className="button button-primary" disabled>
              {t('areas.add')}
            </button>
          ) : (
            <button
              type="button"
              className="button button-primary"
              onClick={() => {
                done(
                  addArea(trip, {
                    name: name.trim() === '' ? picked.label : name,
                    lat: picked.lat,
                    lon: picked.lon,
                    radius,
                    tell,
                  }),
                );
              }}
            >
              {t('areas.add')}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
